import { atom } from "nanostores";
import { ApiError, getMailSummary, getMailThread, markMailRead, postMail, type MailMessage, type MailSummary, type MailThread } from "../net/api";
import { clip } from "../world/markers";
import { $panel, toast } from "./stores";

export type MailInterval = "5m" | "15m" | "30m" | "1h";
const INTERVALS: MailInterval[] = ["5m", "15m", "30m", "1h"];
const INTERVAL_KEY = "nl.mailInterval";

function readInterval(): MailInterval {
  try {
    const v = localStorage.getItem(INTERVAL_KEY);
    if (v && (INTERVALS as string[]).includes(v)) return v as MailInterval;
  } catch { /* private window or blocked storage: use the default */ }
  return "15m";
}

/** How often the copied schedule prompt tells the agent to check (spec 2j §2.2). */
export const $mailInterval = atom<MailInterval>(readInterval());
export function setMailInterval(v: MailInterval): void {
  $mailInterval.set(v);
  try { localStorage.setItem(INTERVAL_KEY, v); } catch { /* remembered for this page only */ }
}

/** The mailbox summary (spec 2j §5); null until the first read. */
export const $mailSummary = atom<MailSummary | null>(null);
export const $mailFailed = atom(false);
export const $mailSelected = atom<string | null>(null);
export interface MailThreadState { agentId: string; messages: MailMessage[]; hasMore: boolean }
export const $mailThread = atom<MailThreadState | null>(null);
/** Unsent text per agent id; it survives closing the panel. */
export const $mailDrafts = atom<Record<string, string>>({});

/** Threads whose unread count rose with the agent writing last: the "回信了" toasts (spec 2j §2.4). */
export function newReplies(prev: MailSummary, next: MailSummary): MailThread[] {
  const was = new Map(prev.threads.map((t) => [t.agent.id, t.unread]));
  return next.threads.filter((t) => t.unread > (was.get(t.agent.id) ?? 0) && !!t.last?.from_agent);
}

/** Merges pages by id (later lists win, they are fresher) and sorts by time; the sort is stable, so
 *  the server's (created_at, id) order holds within a millisecond. */
export function mergeMessages(...lists: MailMessage[][]): MailMessage[] {
  const byId = new Map<string, MailMessage>();
  for (const list of lists) for (const m of list) byId.set(m.id, m);
  return [...byId.values()].sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at));
}

export const threadVisible = (agentId: string) => $panel.get() === "mail" && $mailSelected.get() === agentId;

let summarySeq = 0;

export async function loadMailSummary(): Promise<void> {
  const mine = ++summarySeq;
  try {
    const next = await getMailSummary();
    if (mine !== summarySeq) return; // a newer read is on its way or already applied
    const prev = $mailSummary.get();
    if (prev) {
      for (const t of newReplies(prev, next)) {
        if (!threadVisible(t.agent.id)) toast(`${t.agent.display_name}回信了：${clip(t.last!.preview, 24)}`, 5000);
      }
    }
    $mailSummary.set(next);
    $mailFailed.set(false);
    const sel = $mailSelected.get();
    if (sel && !next.threads.some((t) => t.agent.id === sel)) {
      $mailSelected.set(null);
      $mailThread.set(null);
    }
  } catch {
    if (mine === summarySeq) $mailFailed.set(true);
  }
}

function agentGone(): void {
  $mailSelected.set(null);
  $mailThread.set(null);
  toast("這個 agent 已撤銷");
  void loadMailSummary();
}

/** Reads a thread page; `before` loads older messages in front. A page for an agent that is no longer
 *  selected is dropped (Review Focus 4). */
export async function loadThread(agentId: string, before?: string): Promise<void> {
  try {
    const page = await getMailThread(agentId, before);
    if ($mailSelected.get() !== agentId) return;
    const cur = $mailThread.get();
    if (!cur || cur.agentId !== agentId) {
      $mailThread.set({ agentId, messages: page.messages, hasMore: page.has_more });
    } else if (before) {
      $mailThread.set({ agentId, messages: mergeMessages(page.messages, cur.messages), hasMore: page.has_more });
    } else {
      $mailThread.set({ agentId, messages: mergeMessages(cur.messages, page.messages), hasMore: cur.hasMore });
    }
  } catch (e) {
    if ($mailSelected.get() !== agentId) return;
    if (e instanceof ApiError && e.code === "AGENT_NOT_FOUND") agentGone();
    else toast("信件載入失敗");
  }
}

export function selectAgent(agentId: string): void {
  if ($mailSelected.get() === agentId) return;
  $mailSelected.set(agentId);
  $mailThread.set(null);
  void loadThread(agentId).then(markRead);
}

/** Marks the shown thread read up to its last agent message, only while it is on screen (spec 2j §2.3). */
export async function markRead(): Promise<void> {
  const t = $mailThread.get();
  if (!t || !threadVisible(t.agentId)) return;
  const last = t.messages.filter((m) => m.from_agent).at(-1);
  if (!last || last.read_at) return;
  try {
    await markMailRead(t.agentId, last.id);
  } catch {
    return; // tried again on the next MailChanged or when the panel opens
  }
  const cur = $mailThread.get();
  if (cur?.agentId !== t.agentId) return;
  const upTo = cur.messages.findIndex((m) => m.id === last.id);
  const now = new Date().toISOString();
  $mailThread.set({ ...cur, messages: cur.messages.map((m, i) => (i <= upTo && m.from_agent && !m.read_at ? { ...m, read_at: now } : m)) });
}

export function setDraft(agentId: string, text: string): void {
  $mailDrafts.set({ ...$mailDrafts.get(), [agentId]: text });
}

/** Sends from the panel: null on success, otherwise the error code; the draft stays on failure. */
export async function sendMail(agentId: string, body: string): Promise<string | null> {
  try {
    const m = await postMail(agentId, body);
    const drafts = { ...$mailDrafts.get() };
    delete drafts[agentId];
    $mailDrafts.set(drafts);
    const cur = $mailThread.get();
    if (cur?.agentId === agentId) $mailThread.set({ ...cur, messages: mergeMessages(cur.messages, [m]) });
    return null;
  } catch (e) {
    const code = e instanceof ApiError ? e.code : "NETWORK";
    if (code === "AGENT_NOT_FOUND") agentGone();
    return code;
  }
}

/** WS MailChanged: re-read the summary, and the thread when it is on screen. */
export function onMailChanged(agentId: string): void {
  void loadMailSummary();
  if ($panel.get() === "mail" && $mailThread.get()?.agentId === agentId) void loadThread(agentId).then(markRead);
}

export function checkedText(checkedAt: string | null, now: number): string {
  if (!checkedAt) return "尚未查信";
  const min = Math.floor((now - Date.parse(checkedAt)) / 60000);
  if (min < 1) return "上次查信：剛剛";
  if (min < 60) return `上次查信：${min} 分鐘前`;
  const h = Math.floor(min / 60);
  if (h < 24) return `上次查信：${h} 小時前`;
  return `上次查信：${Math.floor(h / 24)} 天前`;
}

const pad = (n: number) => String(n).padStart(2, "0");

/** "14:05" today, "10/8 14:05" on another day, in the viewer's time zone. */
export function timeText(iso: string, now: number): string {
  const d = new Date(iso), today = new Date(now);
  const hm = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  return d.toDateString() === today.toDateString() ? hm : `${d.getMonth() + 1}/${d.getDate()} ${hm}`;
}
